import { z } from 'zod';
import { entityBase, id, organizationId, timestamp } from './common.js';

/**
 * Scoped RBAC. Authentication (who you are) lives in the platform; this is authorization scoping
 * (what you can do, and where) for dental entities — app-owned data. A user can hold several
 * memberships (e.g. group_admin of group A + location_manager of location X).
 */

export const role = z.enum([
  'platform_admin',
  'group_admin',
  'location_manager',
  'credential_reviewer',
  'professional',
]);
export type Role = z.infer<typeof role>;

export const scopeType = z.enum(['platform', 'group', 'location']);
export type ScopeType = z.infer<typeof scopeType>;

export const membershipStatus = z.enum(['active', 'suspended']);
export type MembershipStatus = z.infer<typeof membershipStatus>;

export const membership = entityBase.extend({
  /** Platform user id this role is granted to. */
  userId: id,
  role,
  scopeType,
  /** group_id or location_id; null for platform scope. */
  scopeId: id.nullable(),
  status: membershipStatus,
});
export type Membership = z.infer<typeof membership>;

export const membershipSummary = membership.pick({ role: true, scopeType: true, scopeId: true });
export type MembershipSummary = z.infer<typeof membershipSummary>;

/** Persona and capability bootstrap returned for the authenticated dental workspace. */
export const membershipSession = z.object({
  professionalId: id.nullable(),
  memberships: z.array(membershipSummary),
  officeCapable: z.boolean(),
  setupUser: z.boolean(),
  canViewTeam: z.boolean(),
  canViewCredentials: z.boolean(),
  canReviewCredentials: z.boolean(),
  canManageTemplates: z.boolean().default(false),
});
export type MembershipSession = z.infer<typeof membershipSession>;

export const createMembershipInput = z.object({
  organizationId,
  userId: id,
  role,
  scopeType,
  scopeId: id.nullish(),
});
export type CreateMembershipInput = z.infer<typeof createMembershipInput>;

export const invitationStatus = z.enum(['pending', 'accepted', 'revoked', 'expired']);
export type InvitationStatus = z.infer<typeof invitationStatus>;

export const invitation = entityBase.extend({
  email: z.string().email(),
  role,
  scopeType,
  scopeId: id.nullable(),
  token: z.string().min(1),
  status: invitationStatus,
  invitedByUserId: id,
  expiresAt: timestamp,
  acceptedAt: timestamp.nullable(),
  acceptedUserId: id.nullable(),
});
export type Invitation = z.infer<typeof invitation>;

export const createInvitationInput = z.object({
  organizationId,
  email: z.string().email(),
  role,
  scopeType,
  scopeId: id.nullish(),
  invitedByUserId: id,
  expiresAt: timestamp,
});
export type CreateInvitationInput = z.infer<typeof createInvitationInput>;

/** Roles that can be granted via invite (not platform_admin). */
export const inviteRole = z.enum([
  'group_admin',
  'location_manager',
  'credential_reviewer',
  'professional',
]);
export type InviteRole = z.infer<typeof inviteRole>;

/** API body for POST /api/invitations — org, inviter, expiry set server-side. */
export const inviteRequestInput = z.object({
  email: z.string().email(),
  role: inviteRole,
  scopeType: scopeType.default('group'),
  scopeId: id.nullish(),
  firstName: z.string().min(1).max(100).optional(),
  lastName: z.string().min(1).max(100).optional(),
});
export type InviteRequestInput = z.infer<typeof inviteRequestInput>;
